/*
 * <custom-the-row-header>
 * Behaviour for the custom luxury header:
 *  - dropdowns: hover/focus (CSS) kept in sync with aria-expanded, plus
 *    click toggling for touch, Escape to close, outside-click to close
 *  - search overlay open/close
 *  - mobile drawer open/close with focus handling + scroll lock
 *  - cart "Bag (n)" live count update + optional cart-drawer open
 *  - sticky "scroll up" reveal and transparent-on-home toggle
 * No external libraries.
 */
(function () {
  'use strict';

  class CustomTheRowHeader extends HTMLElement {
    connectedCallback() {
      this.mqDesktop = window.matchMedia(
        '(min-width: ' + (this.getAttribute('data-breakpoint') || '990') + 'px)'
      );

      this.initDropdowns();
      this.initSearch();
      this.initDrawer();
      this.initCart();
      this.initSticky();

      this.onDocClick = this.onDocClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      this.cartEvents.forEach((name) => document.removeEventListener(name, this.refreshCart));
    }

    /* ----------------------------------------------------------- Dropdowns */
    initDropdowns() {
      // Every element that owns a popover: top-level items and nested items.
      this.dropdownItems = Array.from(
        this.querySelectorAll('.csh-nav__item.csh-has-children, .csh-dropdown__item.csh-has-children')
      );

      this.dropdownItems.forEach((item) => {
        const trigger = item.querySelector(':scope > .csh-nav__link[aria-expanded], :scope > .csh-dropdown__row > [data-csh-toggle]');
        if (!trigger) return;

        // Click = toggle (primary path for touch and the disclosure pattern).
        trigger.addEventListener('click', (e) => {
          // Inside the mobile drawer everything is click-driven (accordion).
          // On desktop, a button trigger toggles; a real link still navigates.
          if (trigger.tagName === 'BUTTON') e.preventDefault();
          const isOpen = item.classList.contains('is-open');
          // Close sibling open items at the same level.
          this.closeSiblings(item);
          if (isOpen) this.closeItem(item);
          else this.openItem(item);
        });
      });
    }

    triggerFor(item) {
      return item.querySelector(
        ':scope > .csh-nav__link[aria-expanded], :scope > .csh-dropdown__row > [data-csh-toggle]'
      );
    }

    setExpanded(item, expanded) {
      const trigger = this.triggerFor(item);
      if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    openItem(item) {
      item.classList.add('is-open');
      this.setExpanded(item, true);
    }

    closeItem(item) {
      item.classList.remove('is-open');
      this.setExpanded(item, false);
      // Close any descendants too.
      item.querySelectorAll('.is-open').forEach((child) => {
        child.classList.remove('is-open');
        this.setExpanded(child, false);
      });
    }

    closeSiblings(item) {
      const parentList = item.parentElement;
      if (!parentList) return;
      Array.from(parentList.children).forEach((sib) => {
        if (sib !== item && sib.classList && sib.classList.contains('is-open')) {
          this.closeItem(sib);
        }
      });
    }

    closeAllDropdowns() {
      this.dropdownItems.forEach((item) => this.closeItem(item));
    }

    /* ------------------------------------------------------------- Search */
    initSearch() {
      // Inline expanding fields: first click reveals the input; once open,
      // clicking the icon submits (if there's text) or closes (if empty).
      this.searchForms = Array.from(this.querySelectorAll('[data-csh-search-form]'));

      this.searchForms.forEach((form) => {
        const toggle = form.querySelector('[data-csh-search-toggle]');
        const input = form.querySelector('[data-csh-search-input]');
        if (!toggle) return;

        toggle.addEventListener('click', (e) => {
          if (!form.classList.contains('is-open')) {
            e.preventDefault();
            this.openSearchForm(form);
          } else if (!input || !input.value.trim()) {
            e.preventDefault();
            this.closeSearchForm(form);
          }
          // open + has text → let the form submit to /search natively
        });
      });
    }

    openSearchForm(form) {
      form.classList.add('is-open');
      const toggle = form.querySelector('[data-csh-search-toggle]');
      const input = form.querySelector('[data-csh-search-input]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (input) window.requestAnimationFrame(() => input.focus());
    }

    closeSearchForm(form) {
      if (!form.classList.contains('is-open')) return;
      form.classList.remove('is-open');
      const toggle = form.querySelector('[data-csh-search-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    closeAllSearch() {
      if (this.searchForms) this.searchForms.forEach((f) => this.closeSearchForm(f));
    }

    /* ------------------------------------------------------- Mobile drawer */
    initDrawer() {
      this.drawer = this.querySelector('[data-csh-drawer]');
      this.drawerOpeners = Array.from(this.querySelectorAll('[data-csh-drawer-open]'));
      const closers = Array.from(this.querySelectorAll('[data-csh-drawer-close]'));

      this.drawerOpeners.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDrawer();
        })
      );
      closers.forEach((btn) => btn.addEventListener('click', () => this.closeDrawer()));

      if (this.drawer) {
        const backdrop = this.drawer.querySelector('[data-csh-drawer-backdrop]');
        if (backdrop) backdrop.addEventListener('click', () => this.closeDrawer());
      }
    }

    toggleDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.contains('is-open') ? this.closeDrawer() : this.openDrawer();
    }

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerOpeners.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.classList.add('csh--overlay-open');
      document.documentElement.classList.add('csh-no-scroll');
      const focusable = this.drawer.querySelector('[data-csh-drawer-close], a, button');
      if (focusable) window.requestAnimationFrame(() => focusable.focus());
    }

    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerOpeners.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.maybeReleaseOverlay();
      document.documentElement.classList.remove('csh-no-scroll');
      const opener = this.drawerOpeners[0];
      if (opener) opener.focus();
    }

    maybeReleaseOverlay() {
      const drawerOpen = this.drawer && this.drawer.classList.contains('is-open');
      if (!drawerOpen) this.classList.remove('csh--overlay-open');
    }

    /* --------------------------------------------------------------- Cart */
    initCart() {
      this.cartCountEls = Array.from(this.querySelectorAll('[data-csh-cart-count]'));
      this.refreshCart = this.refreshCart.bind(this);

      // Optional: open the host theme's cart drawer instead of navigating.
      this.querySelectorAll('[data-csh-cart-open]').forEach((el) =>
        el.addEventListener('click', (e) => {
          if (this.openHostCartDrawer()) e.preventDefault();
        })
      );

      // Live-update the count after AJAX cart changes (covers Vessel + Dawn).
      this.cartEvents = ['cart:update', 'cart:refresh', 'cart:change', 'cart-update', 'cart:added'];
      this.cartEvents.forEach((name) => document.addEventListener(name, this.refreshCart));
    }

    openHostCartDrawer() {
      const drawer =
        document.querySelector('cart-drawer-component') ||
        document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      if (drawer && typeof drawer.showDialog === 'function') {
        drawer.showDialog();
        return true;
      }
      return false;
    }

    refreshCart() {
      if (!window.fetch || !this.cartCountEls.length) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const count = cart.item_count;
          this.cartCountEls.forEach((el) => {
            el.textContent = count;
            const label = el.closest('[data-csh-cart-label]');
            if (label) {
              label.setAttribute('aria-label', 'Bag, ' + count + ' item' + (count === 1 ? '' : 's'));
            }
          });
        })
        .catch(() => {});
    }

    /* ------------------------------------------------------------- Sticky */
    initSticky() {
      const behavior = this.getAttribute('data-sticky'); // always | scroll-up | none
      const transparent = this.getAttribute('data-transparent') === 'true';
      const stickyEnabled = behavior === 'always' || behavior === 'scroll-up';

      // The transparent -> solid swap only makes sense when the header sticks.
      // With sticky OFF, a transparent header just overlays the hero and then
      // scrolls away with the page (no fixed positioning).
      const watchTransparent = transparent && stickyEnabled;

      if (behavior !== 'scroll-up' && !watchTransparent) return;

      let last = window.scrollY;
      this._onScroll = () => {
        const y = window.scrollY;

        if (behavior === 'scroll-up') {
          if (y > last && y > this.offsetHeight) {
            this.classList.add('csh--hidden'); // scrolling down
          } else {
            this.classList.remove('csh--hidden'); // scrolling up
          }
        }

        if (watchTransparent) {
          this.classList.toggle('csh--scrolled', y > 10);
        }

        last = y;
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    /* ------------------------------------------------------ Global handlers */
    onKeydown(e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      const openSearch = this.searchForms && this.searchForms.find((f) => f.classList.contains('is-open'));
      if (openSearch) {
        this.closeSearchForm(openSearch);
        const toggle = openSearch.querySelector('[data-csh-search-toggle]');
        if (toggle) toggle.focus();
        return;
      }
      if (this.drawer && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
        return;
      }
      const open = this.querySelector('.csh-nav__item.is-open, .csh-dropdown__item.is-open');
      if (open) {
        const trigger = this.triggerFor(open) || open.querySelector('[data-csh-toggle], .csh-nav__link');
        this.closeAllDropdowns();
        if (trigger) trigger.focus();
      }
    }

    onDocClick(e) {
      if (!this.contains(e.target)) {
        this.closeAllDropdowns();
      }
      // Close any open (empty) inline search field when clicking outside it.
      if (this.searchForms) {
        this.searchForms.forEach((form) => {
          if (!form.classList.contains('is-open') || form.contains(e.target)) return;
          const input = form.querySelector('[data-csh-search-input]');
          if (!input || !input.value.trim()) this.closeSearchForm(form);
        });
      }
    }
  }

  if (!customElements.get('custom-the-row-header')) {
    customElements.define('custom-the-row-header', CustomTheRowHeader);
  }
})();
